import React, { useState } from 'react';
import { Phone, X, AlertTriangle, MessageCircle, Mail } from 'lucide-react';
export function EmergencyHelpButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [reportType, setReportType] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    description: '',
    contact: '',
    urgent: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would send to a server
    console.log('Report submitted:', {
      reportType,
      ...formData
    });
    setIsSubmitted(true);
    setTimeout(() => {
      handleClose();
    }, 3000);
  };
  const handleClose = () => {
    setIsOpen(false);
    setReportType(null);
    setFormData({
      description: '',
      contact: '',
      urgent: false
    });
    setIsSubmitted(false);
  };
  return <>
      {/* Emergency Button */}
      <button onClick={() => setIsOpen(true)} className="fixed bottom-24 right-4 md:bottom-8 md:right-8 w-14 h-14 bg-gradient-to-br from-red-500 to-pink-600 rounded-full shadow-2xl flex items-center justify-center z-40 hover:scale-110 transition-transform animate-pulse" aria-label="Emergency Help" data-id="element-52">
        <Phone className="w-7 h-7 text-white" data-id="element-53" />
      </button>
      {/* Modal Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn" data-id="element-54">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-55">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-gradient-to-r from-red-500 to-pink-600 p-6 rounded-t-3xl flex items-center justify-between" data-id="element-56">
              <div className="flex items-center space-x-3" data-id="element-57">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-58">
                  <AlertTriangle className="w-6 h-6 text-white" data-id="element-59" />
                </div>
                <div data-id="element-60">
                  <h2 className="text-2xl font-bold text-white" data-id="element-61">
                    Get Help Now
                  </h2>
                  <p className="text-white/90 text-sm" data-id="element-62">
                    You're not alone. We're here to help.
                  </p>
                </div>
              </div>
              <button onClick={handleClose} className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors" aria-label="Close" data-id="element-63">
                <X className="w-6 h-6 text-white" data-id="element-64" />
              </button>
            </div>
            <div className="p-6" data-id="element-65">
              {!isSubmitted ? <>
                  {/* Emergency Contacts */}
                  <div className="mb-6" data-id="element-66">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-67">
                      🚨 Immediate Emergency
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-68">
                      <a href="tel:100" className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl hover:shadow-lg transition-all" data-id="element-69">
                        <Phone className="w-6 h-6 text-red-600 dark:text-red-400" data-id="element-70" />
                        <div data-id="element-71">
                          <p className="font-bold text-red-900 dark:text-red-300" data-id="element-72">
                            Police
                          </p>
                          <p className="text-2xl font-bold text-red-600 dark:text-red-400" data-id="element-73">
                            100
                          </p>
                        </div>
                      </a>
                      <a href="tel:1098" className="flex items-center space-x-3 p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800 rounded-xl hover:shadow-lg transition-all" data-id="element-74">
                        <Phone className="w-6 h-6 text-orange-600 dark:text-orange-400" data-id="element-75" />
                        <div data-id="element-76">
                          <p className="font-bold text-orange-900 dark:text-orange-300" data-id="element-77">
                            Childline
                          </p>
                          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-78">
                            1098
                          </p>
                        </div>
                      </a>
                      <a href="tel:181" className="flex items-center space-x-3 p-4 bg-pink-50 dark:bg-pink-900/20 border-2 border-pink-200 dark:border-pink-800 rounded-xl hover:shadow-lg transition-all" data-id="element-79">
                        <Phone className="w-6 h-6 text-pink-600 dark:text-pink-400" data-id="element-80" />
                        <div data-id="element-81">
                          <p className="font-bold text-pink-900 dark:text-pink-300" data-id="element-82">
                            Women Help
                          </p>
                          <p className="text-2xl font-bold text-pink-600 dark:text-pink-400" data-id="element-83">
                            181
                          </p>
                        </div>
                      </a>
                    </div>
                  </div>
                  {/* Report Form */}
                  {!reportType ? <div data-id="element-84">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-85">
                        Report an Issue
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-86">
                        {[{
                  id: 'abuse',
                  label: 'Abuse or Harassment',
                  icon: AlertTriangle,
                  color: 'red'
                }, {
                  id: 'mental-health',
                  label: 'Mental Health Crisis',
                  icon: MessageCircle,
                  color: 'purple'
                }, {
                  id: 'online-safety',
                  label: 'Online Safety Issue',
                  icon: Phone,
                  color: 'blue'
                }, {
                  id: 'other',
                  label: 'Other Concern',
                  icon: Mail,
                  color: 'gray'
                }].map(type => {
                  const Icon = type.icon;
                  return <button key={type.id} onClick={() => setReportType(type.id)} className={`flex items-center space-x-3 p-4 bg-${type.color}-50 dark:bg-${type.color}-900/20 border-2 border-${type.color}-200 dark:border-${type.color}-800 rounded-xl hover:shadow-lg transition-all text-left`} data-id="element-87">
                              <Icon className={`w-6 h-6 text-${type.color}-600 dark:text-${type.color}-400`} data-id="element-88" />
                              <span className={`font-semibold text-${type.color}-900 dark:text-${type.color}-300`} data-id="element-89">
                                {type.label}
                              </span>
                            </button>;
                })}
                      </div>
                    </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-90">
                      <div className="flex items-center justify-between mb-4" data-id="element-91">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-92">
                          Submit Report
                        </h3>
                        <button type="button" onClick={() => setReportType(null)} className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-93">
                          ← Back
                        </button>
                      </div>
                      <div data-id="element-94">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-95">
                          Describe what happened
                        </label>
                        <textarea value={formData.description} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} required rows={4} className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 dark:bg-slate-700 dark:text-white resize-none" placeholder="Share as much detail as you feel comfortable with..." data-id="element-96" />
                      </div>
                      <div data-id="element-97">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-98">
                          Contact (optional)
                        </label>
                        <input type="text" value={formData.contact} onChange={e => setFormData({
                  ...formData,
                  contact: e.target.value
                })} className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 dark:bg-slate-700 dark:text-white" placeholder="Email or phone (if you want a follow-up)" data-id="element-99" />
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-100">
                        <input type="checkbox" id="urgent" checked={formData.urgent} onChange={e => setFormData({
                  ...formData,
                  urgent: e.target.checked
                })} className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500" data-id="element-101" />
                        <label htmlFor="urgent" className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-102">
                          This is urgent and needs immediate attention
                        </label>
                      </div>
                      <div className="flex space-x-3" data-id="element-103">
                        <button type="submit" className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-bold rounded-xl hover:shadow-lg transition-all" data-id="element-104">
                          Submit Report
                        </button>
                        <button type="button" onClick={handleClose} className="px-6 py-3 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors" data-id="element-105">
                          Cancel
                        </button>
                      </div>
                    </form>}
                  {/* Privacy Note */}
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-lg" data-id="element-106">
                    <p className="text-sm text-blue-900 dark:text-blue-200" data-id="element-107">
                      <strong data-id="element-108">🔒 Your Privacy:</strong> All reports are
                      confidential. We're here to help, not judge.
                    </p>
                  </div>
                </> : <div className="text-center py-8" data-id="element-109">
                  <div className="text-6xl mb-4" data-id="element-110">✅</div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-111">
                    Report Submitted
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-112">
                    Thank you for reaching out. Someone will review this and get
                    back to you if needed.
                  </p>
                  <button onClick={handleClose} className="px-8 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-bold rounded-xl hover:shadow-lg transition-all" data-id="element-113">
                    Close
                  </button>
                </div>}
            </div>
          </div>
        </div>}
    </>;
}